import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import { Box, Typography, Button, InputLabel, Stack, TextField, Divider, Switch } from "@mui/material";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { addOrUpdatePartnership, deletePartnership, notifyPartnership, getPartnershipById } from "../../Requests/PartnershipService";
import { useNavigate } from "react-router-dom";
import { PartnershipsStoreContext } from "../../Store/PartnershipsStore";
import AvatarDropzone from "../StyledComponents/AvatarDropzone/AvatarDropzone";
import ImageDropzone from "../StyledComponents/AvatarDropzone/ImageDropzone.tsx";
import { DeleteForever, NotificationsActive, ArrowBack, FileDownload } from "@mui/icons-material";
import { exportDataToCsv } from "../../Utils/exportTableToCsv.js";
import CircularProgressView from "../StyledComponents/CircularProgressView";
import { Partnership } from "../../Models/Partnership";
import HtmlEditor from '../HtmlEditorView/HtmlEditor.jsx';


export default function PartnershipInfo() {

  const navigate = useNavigate()
  const { partnershipid } = useParams()
  const store = useContext(PartnershipsStoreContext)
  const [isLoading, setIsLoading] = useState(true)

  const [logoImage, setLogoImage] = useState()
  const [heroImage, setHeroImage] = useState()
  const [title, setTitle] = useState()
  const [phoneNumber, setPhoneNumber] = useState()
  const [websiteLink, setWebsiteLink] = useState()
  const [description, setDescription] = useState()
  const [linkText, setLinkText] = useState()
  const [linkURL, setLinkURL] = useState()
  const [hidden, setHidden] = useState()
  const [shortDescription, setShortDescription] = useState()
  const [sortingPosition, setSortingPosition] = useState("99")
  const [createdAt, setCreatedAt] = useState()
  const [updatedAt, setUpdatedAt] = useState()

  const headerRef = useRef(null)
  const APP_BAR_HEIGHT = 64
  const [headerHeight, setHeaderHeight] = useState(0)

  useEffect(() => {
    if (!headerRef.current) return

    const updateHeight = () => {
      setHeaderHeight(headerRef.current?.offsetHeight ?? 0)
    }

    updateHeight()

    const observer = new ResizeObserver(updateHeight)
    observer.observe(headerRef.current)

    return () => observer.disconnect()
  }, [])

  const HEADER_BOTTOM_OFFSET = APP_BAR_HEIGHT + headerHeight

  const fetchData = useCallback(async () => {
    const result = await getPartnershipById(partnershipid)

    setLogoImage(result.logoImage)
    setHeroImage(result.heroImage)
    setTitle(result.title)
    setPhoneNumber(result.phoneNumber)
    setWebsiteLink(result.websiteLink)
    setDescription(result.description)
    setLinkText(result.linkText)
    setLinkURL(result.linkURL)
    setHidden(result.hidden)
    setShortDescription(result.shortDescription)
    setSortingPosition(result.sortingPosition != null ? String(result.sortingPosition) : "99")
    setCreatedAt(result.createdAt)
    setUpdatedAt(result.updatedAt)

    setIsLoading(false)
  }, [partnershipid])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleSaveChanges = async () => {
    let partnership = new Partnership()
    partnership.objectId = partnershipid
    partnership.title = title
    partnership.phoneNumber = phoneNumber
    partnership.websiteLink = websiteLink
    partnership.description = description
    partnership.linkText = linkText
    partnership.linkURL = linkURL
    partnership.shortDescription = shortDescription
    partnership.hidden = hidden
    partnership.sortingPosition = Partnership.normalizeSortingPosition(sortingPosition)
    await addOrUpdatePartnership(partnership, logoImage, heroImage)
    // Mark store as needing refresh
    store.needsRefresh = true
    navigate("/partnerships")
  }

  const getPartnershipForExport = () => ({
    objectId: partnershipid,
    logoImage,
    heroImage,
    title,
    phoneNumber,
    websiteLink,
    description,
    linkText,
    linkURL,
    hidden,
    shortDescription,
    sortingPosition,
    createdAt,
    updatedAt,
  })

  const handleHiddenPartnership = async (event) => {
    setHidden(!event)
  }

  const handleDeletePartnership = async () => {
    await deletePartnership(partnershipid)
    // Mark store as needing refresh
    store.needsRefresh = true
    navigate("/partnerships")
  }

  const handleNotification = async () => {
    notifyPartnership(partnershipid, title)
  }

  const loaderView = <CircularProgressView />

  const contentView = <>
    <Box
      ref={headerRef}
      sx={{
        position: "sticky",
        top: APP_BAR_HEIGHT,
        backgroundColor: "background.paper",
        zIndex: 100
      }}
    >
      <Box
        paddingTop={2}
        paddingBottom={2}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
          gap: 1,
          width: "calc(100% - 280px)",
          paddingRight: 2
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate("/partnerships")}
            sx={{ minWidth: "auto", alignSelf: "flex-start" }}
          >
            Back
          </Button>
          <Typography
            variant="h4"
            sx={{
              maxWidth: "600px"
            }}
          >
            {title}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          onClick={() => handleSaveChanges()}
        >
          Save Changes
        </Button>
      </Box>
      <Divider />
    </Box>


    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between"
      }}
    >
      <Box
        width="calc(100% - 280px)"
        sx={{
          paddingRight: 2
        }}
      >
        <AvatarDropzone
          title={"Logo Image"}
          avatarUrl={logoImage}
          callbackFile={(file) => { setLogoImage(file) }}
        />

        <ImageDropzone
          title={"Hero Image"}
          imageUrl={heroImage}
          callbackFile={(file) => { setHeroImage(file) }}
        />

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Title"}</InputLabel>
          <TextField
            placeholder="Title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Organisation/Company"}</InputLabel>
          <TextField
            placeholder="Organisation/Company"
            value={shortDescription}
            onChange={(event) => setShortDescription(event.target.value)}
          />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Sorting position"}</InputLabel>
          <TextField
            type="number"
            placeholder="1-100"
            value={sortingPosition}
            onChange={(event) => {
              const next = event.target.value
              if (next === "") {
                setSortingPosition("")
                return
              }
              const normalized = Partnership.normalizeSortingPosition(next)
              setSortingPosition(String(normalized))
            }}
            inputProps={{ min: 1, max: 100, step: 1 }}
          />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Phone Number"}</InputLabel>
          <TextField
            placeholder="Phone Number"
            value={phoneNumber}
            onChange={(event) => setPhoneNumber(event.target.value)}
          />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Website Link"}</InputLabel>
          <TextField
            placeholder="Website Link"
            value={websiteLink}
            onChange={(event) => setWebsiteLink(event.target.value)}
          />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Description"}</InputLabel>
          <HtmlEditor initialValue={description} onChange={setDescription} />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Link Text"}</InputLabel>
          <TextField
            placeholder="Link Text"
            value={linkText}
            onChange={(event) => setLinkText(event.target.value)}
          />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Link URL"}</InputLabel>
          <TextField
            placeholder="Link URL"
            value={linkURL}
            onChange={(event) => setLinkURL(event.target.value)}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "280px",
          position: "fixed",
          right: 0,
          top: APP_BAR_HEIGHT + 1,
          bottom: 0,
          overflowY: "auto",
          backgroundColor: "background.paper",
          borderLeft: "1px solid",
          borderColor: "grey.200",
          zIndex: 110
        }}
      >
        <Divider orientation="vertical" />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            padding: 2
          }}
        >
          <Button
            size="small"
            startIcon={<FileDownload />}
            onClick={() =>
              exportDataToCsv([getPartnershipForExport()], partnershipid ? `partnership-${partnershipid}` : "partnership", {
                preferredKeys: ["title"],
              })
            }
            sx={{ mb: 2, alignSelf: "flex-start" }}
          >
            Export CSV
          </Button>
          <Box
            width="100%"
            display="flex"
            justifyContent="space-between"
          >
            <InputLabel>{"Date Created"}</InputLabel>
            <InputLabel>{dayjs(createdAt).format('DD/MMM/YYYY')}</InputLabel>
          </Box>

          <Box
            marginTop={1}
            width="100%"
            display="flex"
            justifyContent="space-between"
          >
            <InputLabel>{"Last Update"}</InputLabel>
            <InputLabel>{dayjs(updatedAt).format('DD/MMM/YYYY')}</InputLabel>
          </Box>

          <Box
            marginTop={1}
            marginBottom={1}
            width="100%"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <InputLabel sx={{ flex: 1, minWidth: 0, whiteSpace: "normal" }}>{"Make this partnership public"}</InputLabel>
            <Switch
              checked={!hidden}
              onChange={(event) => handleHiddenPartnership(event.target.checked)}
            />
          </Box>

          <Divider />

          <Box
            sx={{
              marginTop: 2,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}
          >
            <Button
              variant="outlined"
              onClick={() => handleNotification()}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1
                }}
              >
                <NotificationsActive />
                Push Notification
              </Box>
            </Button>
            <Divider />

            <Button
              variant="outlined"
              color="error"
              onClick={() => handleDeletePartnership()}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1
                }}
              >
                <DeleteForever />
                Delete Partnership
              </Box>
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  </>

  return (
    <>
      {isLoading ? loaderView : contentView}
    </>
  )
}
